import { useEffect, useState } from "react"

/**
 * OKS-966 — "is any modal overlay currently open?", for celebration modals
 * that must not steal a creator's clicks mid-task.
 *
 * # Why this exists
 *
 * `ApplicationController#pending_badge_props` already suppresses
 * `pending_badge` while a `pending_rank_up` exists, for a reason stated in
 * its own docstring: two modals mounted together stack, and the
 * later-mounted one intercepts the earlier one's clicks. That precedence is
 * resolved SERVER-side, which works for two shared props and cannot work
 * here — the server has no idea a creator has a sheet, picker or dialog open
 * on the client. So the same rule needs a client-side arm.
 *
 * # Why DOM observation and not a registry
 *
 * The alternative is a context every overlay registers with on open. It is
 * more precise and it rots: it needs an edit per overlay today AND an
 * un-skippable step for every overlay added later, so the first one that
 * forgets silently reintroduces the bug. Observing the DOM inverts that —
 * every Base UI `Popup` (dialog, alert-dialog, sheet, drawer, popover)
 * renders `role="dialog"` or `role="alertdialog"`, so a NEW overlay is
 * covered the day it is written, with nothing to remember.
 *
 * ⚠️ THE CALLER MUST EXCLUDE ITSELF. A celebration modal is itself a
 * `role="dialog"`, so an unexcluded caller sees its own popup, closes,
 * stops being seen, re-opens, forever. {@link CELEBRATION_ATTR} is the
 * marker for that; see `BadgeEarnModal`. Stripping the marker does not
 * restore the old behaviour — it produces the loop.
 *
 * # Cost
 *
 * One `MutationObserver` on `document.body` (subtree childList + the `role`
 * attribute) per mounting component, and a `querySelector` per mutation
 * batch — a single indexed DOM read, not a tree walk. Only celebration
 * modals mount this.
 *
 * @param excludeSelector CSS selector for elements to ignore (the caller's
 *   own popup). Defaults to the celebration marker.
 * @returns true while at least one non-excluded modal overlay is in the DOM.
 */
export const CELEBRATION_ATTR = "data-celebration-overlay"

// Matches an overlay that is OPEN, across BOTH overlay libraries in this app.
// Keying on presence alone was wrong: a `keepMounted` popup stays in the DOM
// when closed, so the celebration would be suppressed globally and silently,
// forever, with every existing test green.
//
// MEASURED, by rendering each primitive and dumping its attributes — not
// inferred from the Tailwind `data-[open]:` variants, which are a styling
// convention and not proof of the DOM:
//
//   Base UI open   → `data-open=""  role="dialog"`            (co-located)
//   Base UI closed → `data-closed="" role="dialog" hidden=""` (keepMounted)
//   vaul  open     → `data-state="open"  role="dialog"`   (removed, OKS-1022)
//   vaul  closed   → `data-state="closed"`                (removed, OKS-1022)
//
// Since OKS-1022 no Crew overlay is vaul (the real-primitive arm in
// `use-overlay-open.test.tsx` measures `ui/drawer`), but `data-state` is the
// convention most non-Base-UI overlay libraries emit. An open-marker selector
// (`[role="dialog"][data-open]`) would silently stop matching such an overlay
// and the celebration would fire straight over it. That is why this matches
// the role and EXCLUDES the closed markers, rather than requiring an open
// marker.
//
// The exclusion form also fails safe: an overlay from some future library
// that carries neither convention still counts as open, so the worst case is
// a celebration deferred slightly too eagerly, never one that steals clicks
// and never one suppressed forever.
const OVERLAY_SELECTOR = ['[role="dialog"]', '[role="alertdialog"]']
  .map(
    (role) =>
      `${role}:not([data-closed]):not([data-state="closed"]):not([hidden])`,
  )
  .join(", ")

function readOverlayOpen(excludeSelector: string): boolean {
  // SSR / non-DOM guard: Inertia renders this shell server-side too.
  if (typeof document === "undefined") return false

  return Array.from(document.querySelectorAll(OVERLAY_SELECTOR)).some(
    (el) => !el.matches(excludeSelector),
  )
}

export function useOverlayOpen(
  excludeSelector = `[${CELEBRATION_ATTR}]`,
): boolean {
  // ⚠️ LAZY INITIALISER, NOT `useState(false)` — this is a correctness
  // requirement, not a micro-optimisation. Starting false means the first
  // render says "clear", so a celebration modal MOUNTS OPEN over the
  // creator's sheet and only closes once the effect runs: a visible flash
  // of exactly the click-stealing overlay this hook exists to prevent, on
  // every render. Reading synchronously here makes the first render already
  // correct. (It also keeps the modal from entering its close-out
  // animation, which under jsdom never completes — the failure that caught
  // this.)
  const [overlayOpen, setOverlayOpen] = useState(() =>
    readOverlayOpen(excludeSelector),
  )

  useEffect(() => {
    if (typeof document === "undefined") return

    // Re-read on mount: the DOM can change between the render-phase read
    // above and the effect firing.
    const read = () => setOverlayOpen(readOverlayOpen(excludeSelector))

    read()

    const observer = new MutationObserver(read)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // ⚠️ `role` ALONE IS NOT ENOUGH. For an unmounting overlay, open/close
      // is node insertion/removal and `childList` catches it. For a
      // `keepMounted` one it is an ATTRIBUTE FLIP on a node that never moves
      // and whose `role` never changes — so without these the observer never
      // re-reads, and the hook silently degrades to a mount-time-only check
      // (the exact defect the lazy initialiser above exists to prevent,
      // wearing a different costume).
      attributeFilter: [
        "role",
        "data-open",
        "data-closed",
        "data-state",
        "hidden",
      ],
    })

    return () => observer.disconnect()
  }, [excludeSelector])

  return overlayOpen
}
